/* ==========================================================================
   Ecoline — app.js (Foundation)
   Global window.Ecoline per DESIGN-SPEC §5. Vanilla JS, no dependencies,
   defer-safe. Reads window.EcolineSettings injected by layout/theme.liquid.
   ========================================================================== */
(function () {
  'use strict';

  function settings() {
    return window.EcolineSettings || {};
  }

  /* ------------------------------------------------------------------------
     Money formatting
     Uses the shop money format ("₹{{ amount }}" etc.). Whole-rupee display:
     a trailing zero-decimal (".00" / ",00") is stripped after formatting so
     the output byte-matches the theme's `| money_without_trailing_zeros`
     SSR output (western thousands grouping, decimals only when non-zero) —
     a JS re-render never visibly changes a server-rendered price string.
     If the store's format is already amount_no_decimals, behaviour is
     unchanged.
     ------------------------------------------------------------------------ */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseFloat(cents.replace(/[^0-9.-]/g, ''));
    }
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;

    var format = settings().moneyFormat || '₹{{ amount }}';
    var placeholderRe = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRe);
    var key = match ? match[1] : 'amount';
    var value = cents / 100;
    var out;
    var parts;

    switch (key) {
      case 'amount_no_decimals':
        out = Math.round(value).toLocaleString('en-IN');
        break;
      case 'amount_with_comma_separator':
        parts = value.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        out = parts[0] + ',' + parts[1];
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        break;
      default: /* amount */
        out = value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
    }

    /* Whole rupees only: drop a zero-decimal tail (".00" for amount, ",00"
       for amount_with_comma_separator). Non-zero decimals are kept, same as
       Liquid's money_without_trailing_zeros. Grouping never produces a
       2-digit final group, so this cannot eat a thousands separator. */
    out = out.replace(/[.,]00$/, '');

    return match ? format.replace(placeholderRe, out) : format + out;
  }

  /* Money WITHOUT the leading currency symbol — for UI that draws its own
     glyph (the header's ₹ cart-total pill, whose symbol is the
     .el-header__total-mark circle). JS twin of snippets/money-amount.liquid:
     everything before the first digit is dropped, which covers both money
     formats this store has used ("₹2,499" and "Rs. 2,499") without a chain
     that no-ops — and needs no edit if the owner switches format again. Keep
     the two implementations in step. */
  function formatMoneyAmount(cents) {
    return formatMoney(cents).replace(/^[^\d]+/, '').trim();
  }

  /* ------------------------------------------------------------------------
     Cart API — POST /cart/add.js, /cart/change.js, GET /cart.js.
     Every mutation dispatches `ecoline:cart-updated` on document with the
     latest cart JSON as event.detail.
     ------------------------------------------------------------------------ */
  function dispatchCartUpdated(cartJson) {
    if (cartJson && typeof cartJson.item_count === 'number' && window.EcolineSettings) {
      window.EcolineSettings.cartCount = cartJson.item_count;
    }
    document.dispatchEvent(new CustomEvent('ecoline:cart-updated', { detail: cartJson }));
    return cartJson;
  }

  function requestJson(url, options) {
    return fetch(url, options).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var err = new Error((json && (json.description || json.message)) || 'Cart request failed');
          err.status = res.status;
          err.data = json;
          throw err;
        }
        return json;
      });
    });
  }

  var cart = {
    get: function () {
      return requestJson('/cart.js', { headers: { Accept: 'application/json' } });
    },
    add: function (variantId, qty) {
      return requestJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty == null ? 1 : qty })
      }).then(function () {
        /* add.js returns the line item, not the cart — re-fetch, then announce */
        return cart.get();
      }).then(dispatchCartUpdated);
    },
    change: function (lineKey, qty) {
      return requestJson('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      }).then(dispatchCartUpdated);
    }
  };

  /* ------------------------------------------------------------------------
     Drawers — toggles [data-drawer="menu"|"cart"] with .is-open, a shared
     overlay, body scroll lock, Esc + overlay click to close. While a drawer
     is open the rest of the page is made inert (aria-modal contract) and a
     keydown trap cycles Tab/Shift+Tab inside the drawer for browsers
     without `inert` support.
     ------------------------------------------------------------------------ */
  var lastFocused = null;

  /* Everything OUTSIDE the open drawer's ancestor chain becomes inert +
     aria-hidden (marker attribute so only what we set gets cleared — the
     filter drawer lives INSIDE #MainContent, so a fixed target list like
     "main + header" would inert the dialog itself). */
  function setSiblingsInert(el) {
    var node = el;
    while (node && node.parentElement && node !== document.body.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, function (sib) {
        if (sib === node) return;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sib.tagName)) return;
        if (sib.hasAttribute('data-drawer') || sib.hasAttribute('data-drawer-overlay')) return;
        if (sib.hasAttribute('inert') || sib.getAttribute('aria-hidden') === 'true') return;
        sib.setAttribute('data-el-inerted', '');
        sib.setAttribute('inert', '');
        sib.setAttribute('aria-hidden', 'true');
      });
      if (node.parentElement === document.body) break;
      node = node.parentElement;
    }
  }

  function clearInert() {
    document.querySelectorAll('[data-el-inerted]').forEach(function (el) {
      el.removeAttribute('data-el-inerted');
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });
  }

  function drawerFocusables(el) {
    var nodes = el.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
    return Array.prototype.filter.call(nodes, function (n) {
      if (n.disabled || n.getAttribute('tabindex') === '-1') return false;
      if (n.closest('[hidden]')) return false;
      return n.offsetWidth > 0 || n.offsetHeight > 0 || n === document.activeElement;
    });
  }

  /* Tab/Shift+Tab wrap inside the drawer (fallback for browsers without
     `inert`; harmless alongside it). Bound once per drawer element. */
  function onDrawerTrapKeydown(e) {
    if (e.key !== 'Tab') return;
    var el = e.currentTarget;
    if (!el.classList.contains('is-open')) return;
    var items = drawerFocusables(el);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === el) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function getOverlay() {
    var overlay = document.querySelector('[data-drawer-overlay]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.setAttribute('data-drawer-overlay', '');
      overlay.className = 'el-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
    }
    if (!overlay.elBound) {
      overlay.addEventListener('click', function () { drawer.closeAll(); });
      overlay.elBound = true;
    }
    return overlay;
  }

  function openDrawers() {
    return document.querySelectorAll('[data-drawer].is-open');
  }

  var drawer = {
    open: function (id) {
      var el = document.querySelector('[data-drawer="' + id + '"]');
      if (!el) return;
      /* only one drawer at a time */
      openDrawers().forEach(function (other) {
        if (other !== el) {
          other.classList.remove('is-open');
          other.setAttribute('aria-hidden', 'true');
        }
      });
      lastFocused = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      getOverlay().classList.add('is-open');
      document.body.classList.add('el-scroll-lock');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      /* Modal contract: inert the background, trap Tab inside the dialog. */
      clearInert();
      setSiblingsInert(el);
      if (!el.__elTrapBound) {
        el.addEventListener('keydown', onDrawerTrapKeydown);
        el.__elTrapBound = true;
      }
      /* Focus after the open transition has started — synchronously the
         drawer is still visibility:hidden and focus() silently fails,
         leaving screen readers stranded behind an aria-modal dialog. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        });
      });
    },
    close: function (id) {
      var el = document.querySelector('[data-drawer="' + id + '"]');
      if (el) {
        el.classList.remove('is-open');
        el.setAttribute('aria-hidden', 'true');
      }
      if (openDrawers().length === 0) {
        var overlay = document.querySelector('[data-drawer-overlay]');
        if (overlay) overlay.classList.remove('is-open');
        document.body.classList.remove('el-scroll-lock');
        clearInert();
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
        lastFocused = null;
      }
    },
    closeAll: function () {
      openDrawers().forEach(function (el) {
        el.classList.remove('is-open');
        el.setAttribute('aria-hidden', 'true');
      });
      var overlay = document.querySelector('[data-drawer-overlay]');
      if (overlay) overlay.classList.remove('is-open');
      document.body.classList.remove('el-scroll-lock');
      clearInert();
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
      }
      lastFocused = null;
    },
    toggle: function (id) {
      var el = document.querySelector('[data-drawer="' + id + '"]');
      if (!el) return;
      if (el.classList.contains('is-open')) drawer.close(id);
      else drawer.open(id);
    }
  };

  /* ------------------------------------------------------------------------
     Free-shipping progress
     shipProgress(totalPaise) -> { msg, pct, free }
     Threshold in rupees from EcolineSettings.freeShipMin (default 999).
     A threshold of 0/blank/invalid falls back to 999 — the SAME semantic as
     the Liquid consumers (snippets/cart-drawer.liquid and
     sections/main-cart.liquid both substitute 99900 paise when
     `free_min_paise <= 0`), so the SSR first paint and this client render
     never disagree about the progress band.
     ------------------------------------------------------------------------ */
  function shipProgress(totalPaise) {
    if (typeof totalPaise !== 'number' || isNaN(totalPaise) || totalPaise < 0) totalPaise = 0;
    var minRupees = Number(settings().freeShipMin);
    if (isNaN(minRupees) || minRupees <= 0) minRupees = 999;
    var minPaise = Math.round(minRupees * 100);
    var free = totalPaise >= minPaise;
    var pct = Math.min(100, Math.round((totalPaise / minPaise) * 100));
    var msg = free
      ? 'Free shipping unlocked'
      : 'You’re ' + formatMoney(minPaise - totalPaise) + ' away from free shipping';
    return { msg: msg, pct: pct, free: free };
  }

  /* ------------------------------------------------------------------------
     Pincode check (PDP)
     validate(pin) -> { valid, pin, message, note }
     Valid pins persist to localStorage key `ecoline_pin`.
     ------------------------------------------------------------------------ */
  var PIN_RE = /^[1-9][0-9]{5}$/;

  var pincode = {
    pattern: PIN_RE,
    validate: function (pin) {
      pin = String(pin == null ? '' : pin).replace(/\s+/g, '');
      var valid = PIN_RE.test(pin);
      if (valid) {
        try { localStorage.setItem('ecoline_pin', pin); } catch (e) { /* noop */ }
      }
      return {
        valid: valid,
        pin: pin,
        message: valid
          ? '✓ ' + pin + ' — Delivers in 4–6 days.'
          : 'Enter a valid 6-digit pincode.',
        note: valid ? 'Free delivery · flat-pack box' : ''
      };
    },
    saved: function () {
      try { return localStorage.getItem('ecoline_pin') || ''; } catch (e) { return ''; }
    }
  };

  /* ------------------------------------------------------------------------
     Header shrink on scroll
     Toggles `is-scrolled` on <html> when window.scrollY > 24, so the sticky
     header sections (section-header.css) can shrink their padding. rAF-
     throttled to at most one update per frame; only touches classList when
     the state actually changes to avoid layout thrash.
     ------------------------------------------------------------------------ */
  var headerShrinkTicking = false;

  function updateHeaderShrink() {
    headerShrinkTicking = false;
    var shouldShrink = window.scrollY > 24;
    var root = document.documentElement;
    var isShrunk = root.classList.contains('is-scrolled');
    if (shouldShrink && !isShrunk) {
      root.classList.add('is-scrolled');
    } else if (!shouldShrink && isShrunk) {
      root.classList.remove('is-scrolled');
    }
  }

  function onHeaderScroll() {
    if (headerShrinkTicking) return;
    headerShrinkTicking = true;
    window.requestAnimationFrame(updateHeaderShrink);
  }

  /* ------------------------------------------------------------------------
     Expose global
     ------------------------------------------------------------------------ */
  window.Ecoline = {
    formatMoney: formatMoney,
    formatMoneyAmount: formatMoneyAmount,
    cart: cart,
    drawer: drawer,
    shipProgress: shipProgress,
    pincode: pincode
  };

  /* ------------------------------------------------------------------------
     DOM wiring (defer-safe)
     ------------------------------------------------------------------------ */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    /* Esc closes any open drawer */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') drawer.closeAll();
    });

    /* Optional convenience triggers:
       <button data-drawer-open="cart"> / <button data-drawer-close> */
    document.addEventListener('click', function (e) {
      var opener = e.target.closest ? e.target.closest('[data-drawer-open]') : null;
      if (opener) {
        e.preventDefault();
        drawer.open(opener.getAttribute('data-drawer-open'));
        return;
      }
      var closer = e.target.closest ? e.target.closest('[data-drawer-close]') : null;
      if (closer) {
        e.preventDefault();
        var host = closer.closest('[data-drawer]');
        if (host) drawer.close(host.getAttribute('data-drawer'));
        else drawer.closeAll();
      }
    });

    /* Keep any [data-cart-count] badges in sync with the cart: textContent,
       the hide-at-zero .is-empty class, and the host button's accessible
       name (the badge itself is aria-hidden, so the aria-label is the only
       count screen readers hear — it must not go stale after AJAX adds). */
    document.addEventListener('ecoline:cart-updated', function (e) {
      var cartJson = e.detail;
      if (!cartJson || typeof cartJson.item_count !== 'number') return;
      var count = cartJson.item_count;
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        var host = el.closest('button, a');
        if (host && host.hasAttribute('aria-label')) {
          host.setAttribute('aria-label', 'Open cart, ' + count + (count === 1 ? ' item' : ' items'));
        }
      });
    });

    /* Header shrink: run once on init (mid-scroll load, bfcache restore),
       then track scroll to toggle `is-scrolled`. */
    window.addEventListener('scroll', onHeaderScroll, { passive: true });
    updateHeaderShrink();
  });
})();
